import { Briefcase, Target, DollarSign, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

export default function MatchBreakdown({ breakdown, matchScore, compact = false, isDark = false }) {
  const [expanded, setExpanded] = useState(false);

  if (!breakdown) return null;

  // Theme-aware colors
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textAccent = isDark ? 'text-blue-400' : 'text-blue-600';
  const borderColor = isDark ? 'border-slate-600' : 'border-slate-200';
  const progressBg = isDark ? 'bg-slate-700' : 'bg-slate-200';

  const factors = [
    { 
      key: 'industry', 
      label: 'Industry Fit', 
      maxScore: 40, 
      score: breakdown.industry,
      icon: Briefcase,
      description: 'Shared industry focus'
    },
    { 
      key: 'stage', 
      label: 'Stage Match', 
      maxScore: 30, 
      score: breakdown.stage,
      icon: Target,
      description: 'Aligned growth stage'
    },
    { 
      key: 'funding', 
      label: 'Funding Fit', 
      maxScore: 20, 
      score: breakdown.funding,
      icon: DollarSign,
      description: 'Compatible ticket size'
    },
    { 
      key: 'geography', 
      label: 'Location', 
      maxScore: 10, 
      score: breakdown.geography,
      icon: Globe,
      description: 'Geographic alignment'
    },
  ];

  // Compact view - just show colored dots
  if (compact) {
    return (
      <div className="flex items-center gap-1 mt-2">
        {factors.map(factor => {
          const percentage = (factor.score / factor.maxScore) * 100;
          const Icon = factor.icon;
          return (
            <div
              key={factor.key}
              className={`w-6 h-6 rounded-full flex items-center justify-center ${
                percentage >= 75 ? 'bg-green-100 text-green-600' :
                percentage >= 50 ? 'bg-blue-100 text-blue-600' :
                percentage > 0 ? 'bg-yellow-100 text-yellow-600' :
                isDark ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'
              }`}
              title={`${factor.label}: ${factor.score}/${factor.maxScore}`}
            >
              <Icon size={12} />
            </div>
          );
        })}
      </div>
    );
  }

  // Expanded view with full breakdown
  return (
    <div className={`mt-3 border-t pt-3 ${borderColor}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center justify-between w-full text-sm font-medium ${textSecondary} hover:${textAccent}`}
      >
        <span>Why this match?</span>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      
      {expanded && (
        <div className="mt-3 space-y-2">
          {factors.map(factor => {
            const percentage = (factor.score / factor.maxScore) * 100;
            const Icon = factor.icon;
            return (
              <div key={factor.key} className="flex items-center gap-3">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                  percentage >= 75 ? 'bg-green-100 text-green-600' :
                  percentage >= 50 ? 'bg-blue-100 text-blue-600' :
                  percentage > 0 ? 'bg-yellow-100 text-yellow-600' :
                  isDark ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'
                }`}>
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${textPrimary}`}>{factor.label}</span>
                    <span className={`text-xs ${textSecondary}`}>{factor.score}/{factor.maxScore}</span>
                  </div>
                  <div className={`h-1.5 rounded-full overflow-hidden ${progressBg}`}>
                    <div 
                      className={`h-full rounded-full transition-all ${
                        percentage >= 75 ? 'bg-green-500' :
                        percentage >= 50 ? 'bg-blue-500' :
                        percentage > 0 ? 'bg-yellow-500' :
                        'bg-slate-300'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          <p className={`text-xs mt-2 pt-2 border-t ${borderColor} ${textAccent}`}>
            Total compatibility: <span className="font-semibold">{matchScore}%</span>
          </p>
        </div>
      )}
    </div>
  );
}
